import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import BareAuthOnly from './components/BareAuthOnly'
import { ErrorBoundary } from './components/ErrorBoundary'
import Layout from './components/Layout'
import { AuthProvider, useAuth } from './hooks/useAuth'
import AdminApiKeyPage from './pages/admin/AdminApiKeyPage'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import BuiltinPromptsPage from './pages/admin/BuiltinPromptsPage'
import OrgListPage from './pages/admin/OrgListPage'
import OrgPromptsPage from './pages/admin/OrgPromptsPage'
import OrgSettingsPage from './pages/admin/OrgSettingsPage'
import PlatformSettingsPage from './pages/admin/PlatformSettingsPage'
import ProjectAccessPage from './pages/admin/ProjectAccessPage'
import ProviderTemplatesPage from './pages/admin/ProviderTemplatesPage'
import DashboardPage from './pages/DashboardPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import InvitationSignInPage from './pages/InvitationSignInPage'
import LoginPage from './pages/LoginPage'
import McpSetupPage from './pages/McpSetupPage'
import { MemoryPage } from './pages/MemoryPage'
import OAuth2ConsentPage from './pages/OAuth2ConsentPage'
import OAuth2LoginPage from './pages/OAuth2LoginPage'
import OAuthComplete from './pages/OAuthComplete'
import OAuthDenied from './pages/OAuthDenied'
import OAuthPending from './pages/OAuthPending'
import ObservabilityPage from './pages/ObservabilityPage'
import ProfilePage from './pages/ProfilePage'
import ProjectDetailPage from './pages/ProjectDetailPage'
import ProjectSetupPage from './pages/ProjectSetupPage'
import ProjectsPage from './pages/ProjectsPage'
import PromptsPage from './pages/PromptsPage'
import RegisterPage from './pages/RegisterPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import SearchPage from './pages/SearchPage'
import SelectOrgPage from './pages/SelectOrgPage'
import SettingsPage from './pages/SettingsPage'
import UploadPage from './pages/UploadPage'

// Routes are flat. When multi-tenancy is on, React Router's `basename`
// (set in main.tsx to `/<orgSlug>`) makes every absolute path here resolve
// under the current org, so no route needs an explicit `/:orgSlug` prefix.

function isAdminLike(role: string | undefined): boolean {
  return role === 'admin' || role === 'superadmin'
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center app-sky">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    )
  }

  if (!isAdminLike(user?.role)) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center app-sky">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    )
  }

  if (user?.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function SettingsRedirect() {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-500" />
      </div>
    )
  }

  return <Navigate to={isAdminLike(user?.role) ? '/admin/settings' : '/dashboard'} replace />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center app-sky">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center app-sky">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

export default function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <Routes>
          <Route path="/invitation/:orgSlug/:token" element={<InvitationSignInPage />} />
          {/* Auth pages live only at bare URLs. BareAuthOnly enforces that at
              the route, so an in-app `<Navigate to="/login">` under a
              `/<orgSlug>` basename (e.g. from ProtectedRoute below) escapes to
              the bare form instead of rendering at `/<slug>/login`. The
              bootstrap guard in main.tsx only covers full page loads. */}
          <Route
            element={
              <BareAuthOnly>
                <Outlet />
              </BareAuthOnly>
            }
          >
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            {/* Post-authentication org chooser. Not wrapped in PublicRoute:
                the holder has already authenticated but has no session yet —
                they hold a select token and nothing else. */}
            <Route path="/select-org" element={<SelectOrgPage />} />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPasswordPage />
                </PublicRoute>
              }
            />
          </Route>
          {/* Not wrapped in PublicRoute/ProtectedRoute: these run their own
              tokens-from-query-string logic regardless of current auth state. */}
          <Route path="/oauth/complete" element={<OAuthComplete />} />
          <Route path="/oauth/pending" element={<OAuthPending />} />
          <Route path="/oauth/denied" element={<OAuthDenied />} />
          {/* MCP OAuth Hydra bridge (issue #112): the browser arrives from
              Hydra with a challenge query param and no credential, so these
              pages handle signed-in AND signed-out states themselves. */}
          <Route path="/oauth2/login" element={<OAuth2LoginPage />} />
          <Route path="/oauth2/consent" element={<OAuth2ConsentPage />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/:id" element={<ProjectDetailPage />} />
            <Route path="projects/:id/upload" element={<UploadPage />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="memory" element={<MemoryPage />} />
            <Route path="prompts" element={<PromptsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="settings" element={<SettingsRedirect />} />
            <Route
              path="admin/users"
              element={
                <AdminRoute>
                  <AdminUsersPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/settings"
              element={
                <AdminRoute>
                  <SettingsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/observability"
              element={
                <AdminRoute>
                  <ObservabilityPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/api-key"
              element={
                <AdminRoute>
                  <AdminApiKeyPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/access"
              element={
                <AdminRoute>
                  <ProjectAccessPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/org"
              element={
                <AdminRoute>
                  <OrgSettingsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/prompts"
              element={
                <AdminRoute>
                  <OrgPromptsPage />
                </AdminRoute>
              }
            />
            <Route
              path="admin/builtin-prompts"
              element={
                <SuperAdminRoute>
                  <BuiltinPromptsPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="admin/orgs"
              element={
                <SuperAdminRoute>
                  <OrgListPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="admin/platform"
              element={
                <SuperAdminRoute>
                  <PlatformSettingsPage />
                </SuperAdminRoute>
              }
            />
            <Route
              path="admin/provider-templates"
              element={
                <SuperAdminRoute>
                  <ProviderTemplatesPage />
                </SuperAdminRoute>
              }
            />
            <Route path="setup/mcp" element={<McpSetupPage />} />
            <Route path="setup/project" element={<ProjectSetupPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </ErrorBoundary>
    </AuthProvider>
  )
}
